import React, { useState, useEffect } from 'react';
import { Select } from 'antd';
import { Printer, Loader2 } from 'lucide-react';
import { toast } from '@rickylandino/react-messages';
import DOMPurify from 'dompurify';
import Globals from '../../config/globals';
import { getBlankHxSheetReport } from '../../common/services/next-gen/WidgetDataService';
import { getFacilityInterface } from '../../common/services/next-gen/FacilityService';

// Matches what the TinyMCE summary editor allows; anything else (scripts, handlers, styles, links) is stripped.
const SUMMARY_ALLOWED_TAGS = ['p', 'br', 'strong', 'b', 'em', 'i', 'u', 's', 'ul', 'ol', 'li'];

/**
 * Center Details (Maintenance) - lets staff pick one of their mapped centers and print
 * a blank patient history form containing only that center's logo, with no patient
 * fields filled in. Uses pink-api (next-gen) since this is a newly-built feature.
 */
export default function CenterDetails() {
    const associatedFacilities = Globals.associatedFacilities || [];
    const [selectedFacilityId, setSelectedFacilityId] = useState(
        associatedFacilities.length === 1 ? associatedFacilities[0].facilityModel.facilityID : undefined
    );
    const [isPrinting, setIsPrinting] = useState(false);
    const [interfaceInfo, setInterfaceInfo] = useState(null);
    const [isInterfaceLoading, setIsInterfaceLoading] = useState(false);

    useEffect(() => {
        if (!selectedFacilityId) {
            setInterfaceInfo(null);
            return;
        }
        let cancelled = false;
        setIsInterfaceLoading(true);
        getFacilityInterface(selectedFacilityId)
            .then(data => { if (!cancelled) setInterfaceInfo(data); })
            .catch(error => {
                console.error('Error loading facility interface details:', error);
                if (!cancelled) setInterfaceInfo(null);
            })
            .finally(() => { if (!cancelled) setIsInterfaceLoading(false); });
        return () => { cancelled = true; };
    }, [selectedFacilityId]);

    const textColor = Globals.theme === 'dark' ? '#d4d4d8' : '#3f3f46';
    const headingColor = Globals.theme === 'dark' ? '#e4e4e7' : '#18181b';

    const handlePrintBlankHistoryForm = async () => {
        if (!selectedFacilityId) return;
        setIsPrinting(true);
        try {
            const blob = await getBlankHxSheetReport({ facilityId: selectedFacilityId });
            const url = window.URL.createObjectURL(blob);
            window.open(url, '_blank');
            setTimeout(() => window.URL.revokeObjectURL(url), 100);
        } catch (error) {
            console.error('Error generating blank history form:', error);
            toast.error('Failed to generate blank history form. Please try again.');
        } finally {
            setIsPrinting(false);
        }
    };

    return (
        <div className="p-4" style={{ maxWidth: 640 }}>
            <div
                className="rounded-t-lg px-4 py-2"
                style={{
                    backgroundColor: Globals.theme === 'dark' ? 'rgba(236, 72, 153, 0.15)' : '#fce7f3',
                    borderBottom: `1px solid ${Globals.theme === 'dark' ? 'rgba(236, 72, 153, 0.3)' : '#fbcfe8'}`,
                }}
            >
                <span
                    className="text-lg font-bold uppercase"
                    style={{ color: Globals.theme === 'dark' ? '#f9a8d4' : '#be185d', letterSpacing: '0.05em' }}
                >
                    CENTER DETAILS
                </span>
            </div>
            <div
                className="rounded-b-lg p-4"
                style={{
                    backgroundColor: Globals.theme === 'dark' ? '#18181b' : '#ffffff',
                    border: `1px solid ${Globals.theme === 'dark' ? '#3f3f46' : '#e5e7eb'}`,
                    borderTop: 'none',
                }}
            >
                <label
                    className="form-label"
                    style={{ display: 'block', marginBottom: 8, color: Globals.theme === 'dark' ? '#d4d4d8' : '#3f3f46' }}
                >
                    Selected Facility
                </label>
                <Select
                    showSearch
                    style={{ width: '100%', marginBottom: 20 }}
                    placeholder="Select a facility"
                    value={selectedFacilityId}
                    onChange={setSelectedFacilityId}
                    filterOption={(input, option) =>
                        (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
                    }
                    options={associatedFacilities.map(f => ({
                        value: f.facilityModel.facilityID,
                        label: f.facilityModel.facilityNumber
                            ? `${f.facilityModel.facilityNumber} - ${f.facilityModel.facilityName}`
                            : f.facilityModel.facilityName,
                    }))}
                />

                {selectedFacilityId ? (
                    <>
                        <div style={{ marginBottom: 20 }}>
                            <h2 className="text-sm font-semibold" style={{ color: headingColor, marginBottom: 2 }}>Interface Type</h2>
                            <p className="text-sm" style={{ color: textColor, marginBottom: 12 }}>
                                {isInterfaceLoading ? 'Loading...' : (interfaceInfo?.interfaceType || 'Not set')}
                            </p>
                            {interfaceInfo?.interfaceWorkflowSummary && (
                                <>
                                    <h2 className="text-sm font-semibold" style={{ color: headingColor, marginBottom: 2 }}>
                                        Summary of Interface Impact on Workflow
                                    </h2>
                                    <div
                                        className="text-sm"
                                        style={{ color: textColor }}
                                        dangerouslySetInnerHTML={{
                                            __html: DOMPurify.sanitize(interfaceInfo.interfaceWorkflowSummary, {
                                                ALLOWED_TAGS: SUMMARY_ALLOWED_TAGS,
                                                ALLOWED_ATTR: []
                                            })
                                        }}
                                    />
                                </>
                            )}
                        </div>
                        <h2
                            className="text-base font-semibold"
                            style={{ color: Globals.theme === 'dark' ? '#e4e4e7' : '#18181b', marginBottom: 4 }}
                        >
                            Blank History Form
                        </h2>
                        <p
                            className="text-sm"
                            style={{ color: Globals.theme === 'dark' ? '#a1a1aa' : '#71717a', marginBottom: 16, maxWidth: 480 }}
                        >
                            Print a blank patient history form for this center. It includes the center logo in
                            the header but has no patient fields filled in.
                        </p>
                        <button
                            onClick={handlePrintBlankHistoryForm}
                            disabled={isPrinting}
                            className="px-3 py-1.5 rounded-lg font-medium text-sm transition-colors"
                            style={{
                                backgroundColor: isPrinting
                                    ? (Globals.theme === 'dark' ? '#71717a' : '#d1d5db')
                                    : (Globals.theme === 'dark' ? '#ec4899' : '#db2777'),
                                color: 'white',
                                border: 'none',
                                cursor: isPrinting ? 'not-allowed' : 'pointer',
                                display: 'inline-flex',
                                alignItems: 'center',
                                gap: 6,
                            }}
                        >
                            {isPrinting ? (
                                <>
                                    <Loader2 size={16} className="animate-spin" />
                                    Generating...
                                </>
                            ) : (
                                <>
                                    <Printer size={16} />
                                    Print Blank History Form
                                </>
                            )}
                        </button>
                    </>
                ) : (
                    <p className="text-sm" style={{ color: Globals.theme === 'dark' ? '#a1a1aa' : '#71717a' }}>
                        Select a facility to continue.
                    </p>
                )}
            </div>
        </div>
    );
}
